import {
  CorporateFare,
  DriveFileRenameOutline,
  PeopleOutlined,
} from '@mui/icons-material';
import { Box, Card, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { VenueDefaultDatas } from '@/domain/project-venue/entities/project-venue.entity';
import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { createProjectVenuePreset } from '@/domain/project-venue-presets/actions/create-project-venue-preset.action.ts';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { PageTitle } from '@/ui/components/PageTitle';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { DefaultDatasContext } from '@/ui/pages/app/project/venue/default-datas/default-datas-quiz-panel/DefaultDatasContext';
import { DefaultPublicQuiz } from '@/ui/pages/app/project/venue/default-datas/default-datas-quiz-panel/DefaultPublicQuiz';
import { DefaultTechniqueQuiz } from '@/ui/pages/app/project/venue/default-datas/default-datas-quiz-panel/DefaultTechniqueQuiz';

export function VenueDefaultDatas() {
  const project = useAppSelector(selectCurrentProjectVenue);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [panelOpen, setPanelOpen] = useState(false);
  const [formError, setFormError] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState<'public' | 'technique'>(
    'public',
  );
  const [venueDefaultDatas, setVenueDefaultDatas] = useState<VenueDefaultDatas>(
    {
      presetTitle: '',
      avgCapacity: 0,
      mobility: [],
      accommodation: [],
      equipment: [],
    },
  );

  const onClickBack = () => {
    if (project) navigate('/app/projects/venue/' + project.id);
  };

  const handleChangeTitle = (value: string) => {
    setVenueDefaultDatas((prev) => ({ ...prev, presetTitle: value }));
  };

  const handleShowPublic = () => {
    setSelectedQuiz('public');
    setPanelVisible(true);
    setPanelOpen(true);
  };

  const handleShowTechnique = () => {
    setSelectedQuiz('technique');
    setPanelVisible(true);
    setPanelOpen(true);
  };

  const handleSaveDatas = async () => {
    setFormError(false);

    if (!project || !project.organisationId) return;
    if (!venueDefaultDatas.presetTitle) {
      setFormError(true);
      return;
    }
    await dispatch(
      createProjectVenuePreset({
        presetForm: {
          ...venueDefaultDatas,
          organisationId: project.organisationId,
          projectId: project.id,
        },
      }),
    );
    navigate(`/app/projects/venue/${project.id}/dates?force=true`);
  };

  return (
    <DefaultDatasContext.Provider
      value={{
        panelOpen,
        setPanelOpen,
        panelVisible,
        setPanelVisible,
      }}
    >
      <Stack sx={{ gap: 3 }}>
        <PageTitle
          title={translate('ProjectsVenue.DefaultDatas.venue-configuration')}
          backButtonText={project?.name || ''}
          onClickBack={onClickBack}
        />

        <Stack display="flex" alignItems="center">
          <Box maxWidth={540}>
            <Typography
              variant="h6"
              sx={{ fontSize: 34, textAlign: 'center', mb: 3, lineHeight: 1.2 }}
            >
              {translate('ProjectsVenue.DefaultDatas.configure-venue-format')}
            </Typography>

            <Typography sx={{ fontSize: 16, textAlign: 'center', mb: 3 }}>
              {translate(
                'ProjectsVenue.DefaultDatas.this-configuration-will-allow',
              )}
            </Typography>

            <Card
              sx={{
                p: '24px',
                mt: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{ display: 'flex', width: '100%', alignItems: 'center' }}
              >
                <DriveFileRenameOutline sx={{ fontSize: 36, mr: 2 }} />
                <FSTextField
                  sx={{ width: '100%', m: 0 }}
                  value={venueDefaultDatas.presetTitle}
                  error={formError}
                  helperText={
                    formError ? translate('Form.fill-the-field') : undefined
                  }
                  onChange={(e) => handleChangeTitle(e.target.value)}
                  label={translate(
                    'ProjectsVenue.DefaultDatas.configuration-name',
                  )}
                />
              </Box>
            </Card>
            <Card
              sx={{
                p: '24px',
                mt: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex' }}>
                <PeopleOutlined sx={{ fontSize: 36, mr: 2 }} />
                <Typography sx={{ fontSize: 24, fontWeight: 'bold' }}>
                  {translate('ProjectsVenue.DefaultDatas.my-public')}
                </Typography>
              </Box>
              <FSPrimaryButton sx={{ fontSize: 16 }} onClick={handleShowPublic}>
                {translate('ProjectsVenue.DefaultDatas.enter')}
              </FSPrimaryButton>
            </Card>
            <Card
              sx={{
                p: '24px',
                mt: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex' }}>
                <CorporateFare sx={{ fontSize: 36, mr: 2 }} />
                <Typography sx={{ fontSize: 24, fontWeight: 'bold' }}>
                  {translate('ProjectsVenue.DefaultDatas.my-technique')}
                </Typography>
              </Box>
              <FSPrimaryButton
                sx={{ fontSize: 16 }}
                onClick={handleShowTechnique}
              >
                {translate('ProjectsVenue.DefaultDatas.enter')}
              </FSPrimaryButton>
            </Card>

            <Box display="flex" justifyContent="center" mt={6}>
              <FSPrimaryButton sx={{ fontSize: 16 }} onClick={handleSaveDatas}>
                {translate('ProjectsVenue.DefaultDatas.save-configuration')}
              </FSPrimaryButton>
            </Box>
          </Box>
        </Stack>
        {panelVisible && selectedQuiz === 'public' && (
          <DefaultPublicQuiz
            venueDefaultDatas={venueDefaultDatas}
            setVenueDefaultDatas={setVenueDefaultDatas}
            projectId={project?.id}
          />
        )}
        {panelVisible && selectedQuiz === 'technique' && (
          <DefaultTechniqueQuiz
            venueDefaultDatas={venueDefaultDatas}
            setVenueDefaultDatas={setVenueDefaultDatas}
            projectId={project?.id}
          />
        )}
      </Stack>
    </DefaultDatasContext.Provider>
  );
}
